import React, { useState, useEffect, useCallback } from 'react';
import { WebSocketService } from '../services/WebSocketService';
import { ResponsiveButton, ResponsiveStack, ResponsiveLayout, AuthButton, AuthMenu, RoomCodeDisplay } from './index';
import GameInfoPanel from './GameInfoPanel';
import { ShareButton } from './ShareButton';
import { ShareModal } from './ShareModal';
import { useNotifications } from '../hooks/useNotifications';
import { useAuthentication } from '../hooks/useAuthentication';
import { NotificationType } from '../services/NotificationService';
import { GameSessionManager } from '../services/GameSessionManager';
import { deepLinkHandler } from '../services/DeepLinkHandler';
import { GameResult, Player } from '../services/MessageTemplates';
import { AuthMethod } from '../types';
import './WebSocketMultiplayer.css';

interface GameState {
  roomCode: string;
  board: ('X' | 'O' | '')[][];
  currentPlayer: 'X' | 'O';
  gameStatus: 'waiting' | 'playing' | 'won' | 'draw';
  winner?: 'X' | 'O';
  winningLine?: Array<{ row: number; col: number }>;
  winType?: 'row' | 'column' | 'diagonal-main' | 'diagonal-anti';
  playerCount: number;
  players: Array<{
    playerId: string;
    playerSymbol: 'X' | 'O';
  }>;
}

interface WebSocketMultiplayerProps {
  onBackToMenu: () => void;
  initialRoomCode?: string; // Room code to join automatically
}

export const WebSocketMultiplayer: React.FC<WebSocketMultiplayerProps> = ({ onBackToMenu, initialRoomCode }) => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [view, setView] = useState<'menu' | 'waiting' | 'game'>('menu');
  const [roomCode, setRoomCode] = useState('');
  const [inputRoomCode, setInputRoomCode] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentGameSessionId, setCurrentGameSessionId] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalType, setShareModalType] = useState<'room' | 'result'>('room');
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);
  const [showAuthMenu, setShowAuthMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const wsService = WebSocketService.getInstance();
  const gameSessionManager = GameSessionManager.getInstance();
  const { addNotification } = useNotifications();
  const { isAuthenticated, user, login, logout, createProfile, isLoading } = useAuthentication();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleGameUpdate = useCallback((state: GameState) => {
    // Track game start time
    if (state.gameStatus === 'playing' && gameStartTime === null) {
      setGameStartTime(Date.now());
    }
    
    setGameState(state);
    
    // Auto-transition to game when both players are ready
    if (view === 'waiting' && state.gameStatus === 'playing' && state.playerCount >= 2) {
      setView('game');
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started!',
        message: 'Both players connected. Game starting now!',
        duration: 3000
      });
    }

    // Track game completion
    if (state.gameStatus === 'won' || state.gameStatus === 'draw') {
      if (currentGameSessionId) {
        gameSessionManager.endGameSession(currentGameSessionId, true);
      }
    }
  }, [view, addNotification, currentGameSessionId, gameSessionManager, gameStartTime]);

  const handleError = useCallback((error: string) => {
    console.error('🎮 WebSocket error:', error);
    addNotification({
      type: NotificationType.ERROR,
      title: 'Connection Error',
      message: error,
      duration: 5000
    });
  }, [addNotification]);

  const handlePlayAgain = useCallback(() => {
    wsService.playAgain();
    
    addNotification({
      type: NotificationType.INFO,
      title: 'New Game Starting',
      message: 'Starting a new game with the same players...',
      duration: 2000
    });
  }, [addNotification]);

  // Share handling functions
  const handleShareRoom = useCallback(() => {
    setShareModalType('room');
    setShowShareModal(true);
  }, []);

  const handleShareResult = useCallback(() => {
    setShareModalType('result');
    setShowShareModal(true);
  }, []);

  // Auth menu handlers
  const handleAuthButtonClick = () => {
    setShowAuthMenu(!showAuthMenu);
  };

  const handleAuthMenuClose = () => {
    setShowAuthMenu(false);
  };

  const handleAuthMethodSelect = async (method: AuthMethod) => {
    setShowAuthMenu(false);
    
    try {
      const result = await login(method);
      
      if (result.success) {
        addNotification({
          type: NotificationType.SUCCESS,
          title: 'Welcome!',
          message: `Successfully logged in with ${method}`,
          duration: 3000
        });
      } else {
        addNotification({
          type: NotificationType.ERROR,
          title: 'Login Failed',
          message: result.error || 'Unable to log in. Please try again.',
          duration: 5000
        });
      }
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Login Error',
        message: 'An unexpected error occurred. Please try again.',
        duration: 5000
      });
    }
  };

  const handleAuthLogout = async () => {
    // TODO: Show logout confirmation dialog
    try {
      await logout();
      setShowAuthMenu(false);
      
      addNotification({
        type: NotificationType.INFO,
        title: 'Logged Out',
        message: 'You have been successfully logged out.',
        duration: 3000
      });
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Logout Error',
        message: 'Unable to log out. Please try again.',
        duration: 5000
      });
    }
  };

  const handleViewStatistics = () => {
    setShowAuthMenu(false);
    addNotification({
      type: NotificationType.INFO,
      title: 'Coming Soon',
      message: 'Statistics view is under development.',
      duration: 3000
    });
  };

  const handleViewSettings = () => {
    setShowAuthMenu(false);
    addNotification({
      type: NotificationType.INFO,
      title: 'Coming Soon',
      message: 'Settings view is under development.',
      duration: 3000
    });
  };

  const handleViewPreferences = () => {
    setShowAuthMenu(false);
    addNotification({
      type: NotificationType.INFO,
      title: 'Coming Soon',
      message: 'Preferences view is under development.',
      duration: 3000
    });
  };

  const handleViewAbout = () => {
    setShowAuthMenu(false);
    addNotification({
      type: NotificationType.INFO,
      title: 'PlayHub Games AI',
      message: 'Version 1.0.0 - Multi-game platform with AI opponents',
      duration: 5000
    });
  };

  // Email authentication handlers
  const handleEmailSignup = async (email: string, password: string, name?: string) => {
    try {
      const result = await createProfile(AuthMethod.CUSTOM, { email, password });
      
      setShowAuthMenu(false);
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Account Created!',
        message: `Welcome ${name || email}! Your account has been created successfully.`,
        duration: 5000
      });
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Signup Failed',
        message: error instanceof Error ? error.message : 'Unable to create account. Please try again.',
        duration: 5000
      });
      throw error;
    }
  };

  const handleEmailLogin = async (email: string, password: string, rememberMe: boolean) => {
    try {
      const result = await login(AuthMethod.CUSTOM, { email, password });
      
      if (result.success) {
        setShowAuthMenu(false);
        
        addNotification({
          type: NotificationType.SUCCESS,
          title: 'Welcome Back!',
          message: `Successfully logged in as ${email}`,
          duration: 3000
        });
      } else {
        addNotification({
          type: NotificationType.ERROR,
          title: 'Login Failed',
          message: result.error || 'Invalid email or password.',
          duration: 5000
        });
        throw new Error(result.error || 'Login failed');
      }
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Login Error',
        message: error instanceof Error ? error.message : 'An unexpected error occurred.',
        duration: 5000
      });
      throw error;
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Email Sent',
        message: `Password reset link sent to ${email}`,
        duration: 5000
      });
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Reset Failed',
        message: 'Unable to send reset email. Please try again.',
        duration: 5000
      });
      throw error;
    }
  };

  const generateRoomShareData = useCallback(() => {
    // Use roomCode directly if gameState is not available (waiting room)
    const code = gameState?.roomCode || roomCode;
    if (!code) return null;

    return {
      gameType: 'Tic-Tac-Toe',
      roomCode: code,
      playerName: 'Player', // Could be enhanced to get actual player name
      maxPlayers: 2,
      difficulty: 'Multiplayer'
    };
  }, [gameState, roomCode]);

  const generateResultShareData = useCallback((): GameResult | null => {
    if (!gameState || (gameState.gameStatus !== 'won' && gameState.gameStatus !== 'draw')) {
      return null;
    }

    const players: Player[] = gameState.players.map(player => ({
      name: `Player ${player.playerSymbol}`,
      id: player.playerId,
      score: undefined // Could be enhanced with actual scoring
    }));

    const gameDuration = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;

    return {
      gameType: 'Tic-Tac-Toe',
      players,
      winner: gameState.winner ? `Player ${gameState.winner}` : null,
      duration: gameDuration,
      finalState: gameState.board.flat(), // Convert 2D board to 1D array
      achievements: [] // Could be enhanced with achievements
    };
  }, [gameState, gameStartTime]);

  const generateShareableRoomUrl = useCallback(() => {
    // Use roomCode directly if gameState is not available (waiting room)
    const code = gameState?.roomCode || roomCode;
    if (!code) return '';
    
    // Generate simple deep link URL instead of encrypted one
    const baseUrl = window.location.origin;
    return `${baseUrl}/tic-tac-toe?roomCode=${code}`;
  }, [gameState, roomCode]);

  useEffect(() => {
    // Set up the WebSocket handlers
    wsService.onGameUpdate(handleGameUpdate);
    wsService.onErrorOccurred(handleError);

    // DON'T disconnect on unmount - the WebSocket should persist
    // across component remounts (e.g., during navigation)
    // Only disconnect when explicitly leaving (via handleBackToMenu)
    return () => {
      // Cleanup handlers but keep connection alive
      console.log('🔌 WebSocketMultiplayer unmounting, keeping connection alive');
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Update handlers when they change, but don't disconnect
  useEffect(() => {
    wsService.onGameUpdate(handleGameUpdate);
    wsService.onErrorOccurred(handleError);
  }, [handleGameUpdate, handleError]);

  // Auto-join room if initialRoomCode is provided (deep link)
  useEffect(() => {
    if (initialRoomCode && view === 'menu') {
      setInputRoomCode(initialRoomCode);
      
      // Add a small delay to ensure WebSocket is connected
      const joinTimer = setTimeout(() => {
        joinRoom(initialRoomCode);
      }, 1000); // 1 second delay
      
      return () => clearTimeout(joinTimer);
    }
  }, [initialRoomCode, view]);

  const handleBackToMenu = () => {
    // Disconnect when leaving multiplayer
    wsService.disconnect();
    onBackToMenu();
  };

  const handleBackToHub = () => {
    // Disconnect when leaving multiplayer
    wsService.disconnect();
    // Navigate to hub - this would need to be passed as a prop
    onBackToMenu(); // For now, go back to menu, but this could be enhanced
  };

  const createRoom = async () => {
    setIsConnecting(true);
    try {
      const code = await wsService.createRoom();
      setRoomCode(code);
      setView('waiting');
      
      // Start tracking the game session
      const gameSessionId = gameSessionManager.startGameSession('tic-tac-toe-multiplayer');
      setCurrentGameSessionId(gameSessionId);
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Failed to Create Room',
        message: error instanceof Error ? error.message : 'Connection failed',
        duration: 5000
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const joinRoom = async (roomCodeToJoin?: string) => {
    const codeToUse = (roomCodeToJoin || inputRoomCode).trim();
    const isDeepLinkJoin = !!roomCodeToJoin; // True if called from deep link
    
    if (codeToUse.length !== 6) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Invalid Code',
        message: 'Room code must be 6 characters',
        duration: 3000
      });
      return;
    }

    setIsConnecting(true);
    
    // Only show "joining" notification for manual joins, not deep links
    if (!isDeepLinkJoin) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Joining Room',
        message: `Connecting to room ${codeToUse.toUpperCase()}...`,
        duration: 2000
      });
    }
    
    try {
      await wsService.joinRoom(codeToUse.toUpperCase());
      setRoomCode(codeToUse.toUpperCase());
      setView('game'); // Go directly to game - server confirmed both players are connected
      
      // Start tracking the game session
      const gameSessionId = gameSessionManager.startGameSession('tic-tac-toe-multiplayer');
      setCurrentGameSessionId(gameSessionId);
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: isDeepLinkJoin ? 'Room Joined!' : 'Joined Game!',
        message: isDeepLinkJoin 
          ? `Successfully joined room ${codeToUse.toUpperCase()}. Waiting for game to start...`
          : 'Waiting for game to start...',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to join room:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Connection failed';
      if (error instanceof Error) {
        if (error.message.includes('not found') || error.message.includes('does not exist')) {
          errorMessage = isDeepLinkJoin 
            ? 'This room no longer exists or has expired'
            : 'Room not found. Please check the room code.';
        } else if (error.message.includes('full')) {
          errorMessage = 'Room is full. Please try another room.';
        } else if (error.message.includes('already started')) {
          errorMessage = 'Game has already started. Please create a new room.';
        } else {
          errorMessage = error.message;
        }
      }
      
      addNotification({
        type: NotificationType.ERROR,
        title: 'Unable to Join Room',
        message: errorMessage,
        duration: 5000
      });
      
      // If deep link join fails, show the manual join interface
      if (isDeepLinkJoin) {
        setView('menu');
        setInputRoomCode(codeToUse); // Pre-fill the room code for manual retry
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const getLineCoordinates = (winningLine: Array<{ row: number; col: number }>, winType?: string) => {
    if (!winningLine || winningLine.length === 0) return { x1: 0, y1: 0, x2: 0, y2: 0 };
    
    // Board dimensions matching the inline styles
    const cellSize = 100; // Cell width/height
    const gap = 3; // Gap between cells
    const borderWidth = 3; // Cell border width
    
    // Calculate the visual center of each cell
    // Account for borders: each cell has a 3px border, so the actual clickable area is smaller
    const getCellCenter = (row: number, col: number) => {
      // Position of cell including gaps
      const cellX = col * (cellSize + gap);
      const cellY = row * (cellSize + gap);
      
      // Center of the cell (accounting for border being part of the cell)
      const x = cellX + (cellSize / 2);
      const y = cellY + (cellSize / 2);
      
      return { x, y };
    };
    
    const start = winningLine[0];
    const end = winningLine[winningLine.length - 1];
    
    const startPos = getCellCenter(start.row, start.col);
    const endPos = getCellCenter(end.row, end.col);
    
    let x1 = startPos.x;
    let y1 = startPos.y;
    let x2 = endPos.x;
    let y2 = endPos.y;
    
    // Extend line slightly beyond cells for visual effect
    const extension = 15; // pixels - reduced for better alignment
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    if (length > 0) {
      const unitX = dx / length;
      const unitY = dy / length;
      
      x1 -= unitX * extension;
      y1 -= unitY * extension;
      x2 += unitX * extension;
      y2 += unitY * extension;
    }
    
    return { x1, y1, x2, y2 };
  };

  const makeMove = (row: number, col: number) => {
    if (!gameState) return;

    const playerSymbol = wsService.getPlayerSymbol();
    if (!playerSymbol) return;

    // Check if it's this player's turn
    if (gameState.currentPlayer !== playerSymbol) {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Not Your Turn!',
        message: `Wait for Player ${gameState.currentPlayer} to make their move`,
        duration: 2000
      });
      return;
    }

    // Check if cell is empty
    if (gameState.board[row][col] !== '') {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Invalid Move',
        message: 'That cell is already occupied',
        duration: 2000
      });
      return;
    }

    // Build the new game state after this move
    const newBoard = gameState.board.map((r, ri) =>
      r.map((c, ci) => (ri === row && ci === col ? playerSymbol : c))
    );
    const nextPlayer = playerSymbol === 'X' ? 'O' : 'X';
    
    // Check for win/draw
    const winResult = checkWin(newBoard, playerSymbol);
    const isDraw = !winResult && newBoard.every(r => r.every(c => c !== ''));
    
    const newGameState = {
      ...gameState,
      board: newBoard,
      currentPlayer: nextPlayer,
      gameStatus: winResult ? 'won' : isDraw ? 'draw' : 'playing',
      winner: winResult ? playerSymbol : undefined,
      winningLine: winResult || undefined,
    };

    // Update local state immediately
    setGameState(newGameState as any);

    // Send move WITH full game state to server so it broadcasts to opponent
    wsService.send({
      action: 'tictactoe:move',
      roomCode: roomCode,
      move: { row, col, player: playerSymbol },
      gameState: newGameState,
      playerId: `player-${playerSymbol}`
    });
  };

  // Check if a player has won
  const checkWin = (board: string[][], player: string): Array<{row: number, col: number}> | null => {
    const lines = [
      // Rows
      [{row:0,col:0},{row:0,col:1},{row:0,col:2}],
      [{row:1,col:0},{row:1,col:1},{row:1,col:2}],
      [{row:2,col:0},{row:2,col:1},{row:2,col:2}],
      // Columns
      [{row:0,col:0},{row:1,col:0},{row:2,col:0}],
      [{row:0,col:1},{row:1,col:1},{row:2,col:1}],
      [{row:0,col:2},{row:1,col:2},{row:2,col:2}],
      // Diagonals
      [{row:0,col:0},{row:1,col:1},{row:2,col:2}],
      [{row:0,col:2},{row:1,col:1},{row:2,col:0}],
    ];
    
    for (const line of lines) {
      if (line.every(pos => board[pos.row][pos.col] === player)) {
        return line;
      }
    }
    return null;
  };

  const renderBoard = () => {
    if (!gameState) return null;

    const playerSymbol = wsService.getPlayerSymbol();
    const isMyTurn = gameState.currentPlayer === playerSymbol;

    return (
      <div style={{ textAlign: 'center' }}>
        <h3 style={{ color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>Multiplayer Tic-Tac-Toe</h3>
        
        <GameInfoPanel
          items={[
            { label: 'Room:', value: roomCode, icon: '🏠' },
            { label: 'You:', value: playerSymbol || '?', icon: '👤' },
            { label: 'Turn:', value: gameState.currentPlayer, icon: '🎲', highlight: isMyTurn },
            { label: 'Players:', value: `${gameState.playerCount}/2`, icon: '👥' },
          ]}
          turnIndicator={{
            isMyTurn,
            mySymbol: playerSymbol || '?',
            currentPlayer: gameState.currentPlayer,
          }}
          position="right"
        />
        
        {/* Turn indicator is now part of GameInfoPanel on the right */}

        {gameState.gameStatus === 'won' && (
          <div className={`ws-win-announcement win-announcement ${gameState.winner === playerSymbol ? 'victory' : 'defeat'}`} style={{ 
            padding: '20px', 
            borderRadius: '12px', 
            border: '4px solid',
            margin: '15px 0',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <h2 style={{ 
              margin: '0 0 10px 0',
              fontSize: '28px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}>
              {gameState.winner === playerSymbol ? 
                '🎉 VICTORY! 🎉' : 
                '💔 DEFEAT 💔'
              }
            </h2>
            <p style={{ 
              margin: '0',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              {gameState.winner === playerSymbol ? 
                `Congratulations! You won as Player ${playerSymbol}!` : 
                `Player ${gameState.winner} wins! Better luck next time!`
              }
            </p>
          </div>
        )}
        
        {gameState.gameStatus === 'draw' && (
          <div className="ws-draw-announcement" style={{ 
            padding: '20px', 
            borderRadius: '12px', 
            border: '4px solid',
            margin: '15px 0',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <h2 style={{ 
              margin: '0 0 10px 0',
              fontSize: '28px'
            }}>
              🤝 IT'S A DRAW! 🤝
            </h2>
            <p style={{ 
              margin: '0',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              Great game! No winner this time.
            </p>
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px'
        }}>
          <div className="game-board" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 100px)',
            gridTemplateRows: 'repeat(3, 100px)',
            gap: '3px',
            position: 'relative',
            width: '306px', // 3 * 100px + 2 * 3px
            height: '306px',
            marginBottom: '20px'
          }}>
          {gameState.board.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              const canClick = gameState.gameStatus === 'playing' && 
                             cell === '' && 
                             isMyTurn;
              const isClickable = canClick || (gameState.gameStatus === 'playing' && cell === '');
              
              // Check if this cell is part of the winning line
              const isWinningCell = gameState.winningLine?.some(
                pos => pos.row === rowIndex && pos.col === colIndex
              );
              
              return (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  onClick={() => makeMove(rowIndex, colIndex)}
                  disabled={!isClickable}
                  className={`ws-game-cell game-cell ${isWinningCell ? 'winning-cell winning' : ''} ${cell ? '' : 'empty'} ${!canClick ? 'disabled' : ''}`}
                  style={{
                    width: '100px',
                    height: '100px',
                    fontSize: '36px',
                    fontWeight: 'bold',
                    border: isWinningCell 
                      ? '4px solid #ffffff' 
                      : '3px solid var(--ws-cell-border)',
                    borderRadius: '8px',
                    backgroundColor: isWinningCell 
                      ? (gameState.winner === playerSymbol ? '#28a745' : '#dc3545')
                      : cell ? 'var(--ws-cell-bg)' : (canClick ? 'var(--ws-cell-bg-empty)' : 'var(--ws-cell-bg-disabled)'),
                    cursor: canClick ? 'pointer' : 'not-allowed',
                    color: isWinningCell 
                      ? '#ffffff'
                      : cell === 'X' ? 'var(--ws-text-info)' : (cell === 'O' ? 'var(--ws-text-danger)' : 'var(--ws-text-primary)'),
                    transition: 'all 0.3s ease',
                    boxShadow: isWinningCell 
                      ? '0 0 20px rgba(40, 167, 69, 0.6)' 
                      : canClick ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                    transform: isWinningCell ? 'scale(1.05)' : 'scale(1)',
                    boxSizing: 'border-box',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {cell}
                </button>
              );
            })
          )}
          
          {/* Winning Line Overlay */}
          {gameState.gameStatus === 'won' && gameState.winningLine && (() => {
            const coords = getLineCoordinates(gameState.winningLine, gameState.winType);
            return (
              <div
                className="board-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '306px', // 3 cells * 100px + 2 gaps * 3px
                  height: '306px',
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              >
                <svg
                  width="306"
                  height="306"
                  viewBox="0 0 306 306"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  preserveAspectRatio="xMidYMid meet"
                >
                  {/* Background line for contrast */}
                  <line
                    className="winning-line-bg"
                    x1={coords.x1}
                    y1={coords.y1}
                    x2={coords.x2}
                    y2={coords.y2}
                    stroke="#ffffff"
                    strokeWidth="14"
                    strokeLinecap="round"
                    opacity="0.9"
                  />
                  {/* Main winning line */}
                  <line
                    className="winning-line"
                    x1={coords.x1}
                    y1={coords.y1}
                    x2={coords.x2}
                    y2={coords.y2}
                    stroke={gameState.winner === playerSymbol ? '#28a745' : '#dc3545'}
                    strokeWidth="10"
                    strokeLinecap="round"
                    style={{
                      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))'
                    }}
                  />
                </svg>
              </div>
            );
          })()}
          </div>
        </div>

        {/* Game Action Buttons */}
        <div style={{ 
          marginTop: '20px',
          width: '100%',
          maxWidth: '500px',
          margin: '20px auto 0'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '12px' : '10px',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Play Again Button - only show when game is completed */}
            {(gameState.gameStatus === 'won' || gameState.gameStatus === 'draw') && (
              <ResponsiveButton
                variant="primary"
                onClick={handlePlayAgain}
                style={{ 
                  flex: isMobile ? '1' : '0 1 auto',
                  minWidth: isMobile ? '100%' : '140px',
                  height: '48px'
                }}
              >
                🔄 Play Again
              </ResponsiveButton>
            )}
            
            {/* Share Result Button - only show when game is completed */}
            {(gameState.gameStatus === 'won' || gameState.gameStatus === 'draw') && (
              <div style={{ 
                flex: isMobile ? '1' : '0 1 auto',
                minWidth: isMobile ? '100%' : '140px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <ShareButton
                  shareType="result"
                  data={{
                    message: 'Check out my Tic-Tac-Toe game result!',
                    title: 'Tic-Tac-Toe Game Result'
                  }}
                  size="medium"
                  onShareStart={handleShareResult}
                  onShareComplete={(success) => {
                    if (success) {
                      addNotification({
                        type: NotificationType.SUCCESS,
                        title: 'Result Shared!',
                        message: 'Your game result has been shared.',
                        duration: 3000
                      });
                    }
                  }}
                  style={{ 
                    width: isMobile ? '100%' : 'auto',
                    height: '48px',
                    minHeight: '48px'
                  }}
                />
              </div>
            )}
            
            <ResponsiveButton 
              variant="outline" 
              onClick={handleBackToMenu}
              style={{ 
                flex: isMobile ? '1' : '0 1 auto',
                minWidth: isMobile ? '100%' : '140px',
                height: '48px'
              }}
            >
              Back to Menu
            </ResponsiveButton>
          </div>
        </div>
      </div>
    );
  };

  if (view === 'menu') {
    return (
      <div className="websocket-multiplayer-container">
        <ResponsiveLayout maxWidth="sm" centered>
          <ResponsiveStack direction="column" gap="lg" align="center">
            <h2 style={{ color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>🎮 Multiplayer Tic-Tac-Toe</h2>
            
            <ResponsiveButton
              variant="primary"
              onClick={createRoom}
              disabled={isConnecting}
            >
              {isConnecting ? '🔄 Creating...' : '🎯 Create Room (Player X)'}
            </ResponsiveButton>
            
            <div style={{ width: '100%', maxWidth: '300px' }}>
              <input
                type="text"
                inputMode="text"
                placeholder="Enter 6-character room code"
                value={inputRoomCode}
                onChange={(e) => setInputRoomCode(e.target.value.trim().toUpperCase())}
                onBlur={(e) => setInputRoomCode(e.target.value.trim().toUpperCase())}
                maxLength={6}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck="false"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  textAlign: 'center',
                  border: '2px solid var(--ws-border-primary)',
                  borderRadius: '5px',
                  marginBottom: '10px',
                  backgroundColor: 'var(--ws-cell-bg-empty)',
                  color: 'var(--ws-text-primary)'
                }}
              />
              <ResponsiveButton
                variant="primary"
                onClick={() => joinRoom()}
                disabled={inputRoomCode.length !== 6 || isConnecting}
              >
                {isConnecting ? '🔄 Joining...' : 'Join Game (Player O)'}
              </ResponsiveButton>
            </div>
            
            <ResponsiveButton variant="outline" onClick={handleBackToMenu}>
              ← Back to Menu
            </ResponsiveButton>
          </ResponsiveStack>
        </ResponsiveLayout>
      </div>
    );
  }

  if (view === 'waiting') {
    return (
      <div className="websocket-multiplayer-container">
        <ResponsiveLayout maxWidth="sm" centered>
          <RoomCodeDisplay
            roomCode={roomCode}
            gameType="tic-tac-toe"
            playerRole="Player X"
            onCancel={handleBackToMenu}
            showCancelButton={true}
          />
        </ResponsiveLayout>
        
        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareType={shareModalType}
          shareData={generateRoomShareData()}
          onShareComplete={(success) => {
            setShowShareModal(false);
            if (success) {
              addNotification({
                type: NotificationType.SUCCESS,
                title: 'Room Shared!',
                message: 'Your room invitation has been shared.',
                duration: 3000
              });
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="websocket-multiplayer-container">
      <ResponsiveLayout maxWidth="md" centered>
        {renderBoard()}
      </ResponsiveLayout>
      
      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareType={shareModalType}
        shareData={shareModalType === 'room' ? generateRoomShareData() : generateResultShareData()}
        onShareComplete={(success) => {
          setShowShareModal(false);
          if (success) {
            addNotification({
              type: NotificationType.SUCCESS,
              title: 'Shared Successfully!',
              message: `Your ${shareModalType} has been shared to WhatsApp.`,
              duration: 3000
            });
          }
        }}
      />
      
      {/* Auth Menu */}
      <AuthMenu
        isOpen={showAuthMenu}
        isAuthenticated={isAuthenticated}
        user={user}
        onClose={handleAuthMenuClose}
        onAuthMethodSelect={handleAuthMethodSelect}
        onEmailSignup={handleEmailSignup}
        onEmailLogin={handleEmailLogin}
        onPasswordReset={handlePasswordReset}
        onLogout={handleAuthLogout}
        onViewStatistics={handleViewStatistics}
        onViewSettings={handleViewSettings}
        onViewPreferences={handleViewPreferences}
        onViewAbout={handleViewAbout}
        isMobile={isMobile}
        isLoading={isLoading}
      />
    </div>
  );
};

export default WebSocketMultiplayer;